/*
 * Continuous product loading (infinite scroll) for Dawn collection and search grids.
 *
 * Progressive enhancement over Shopify's native pagination:
 *  - Reads the next page URL straight from the server-rendered pagination
 *    (paginate.next.url), so all active filters and the sort order are carried
 *    through automatically.
 *  - Fetches the next page via the Section Rendering API (?section_id=...),
 *    appends the new cards to the existing grid, and reads the following page's
 *    URL from the response.
 *  - Auto-loads as the customer scrolls (IntersectionObserver) with a visible
 *    "Load more cards" button as a fallback.
 *  - Loading state, duplicate-load protection and a clear end state.
 *
 * The custom Add button keeps working with no re-binding: appended cards contain
 * the full <product-form> / quick-add markup, and custom elements auto-upgrade
 * when inserted into the DOM. product-form.js / quick-add.js are already loaded
 * by the section.
 *
 * When filtering or sorting runs, facets.js replaces #ProductGridContainer's
 * innerHTML. That re-creates this element, so a fresh instance initialises with
 * the new first page and a reset next URL. The old instance's observer is torn
 * down in disconnectedCallback.
 */
if (!customElements.get('product-list-infinite')) {
  customElements.define(
    'product-list-infinite',
    class ProductListInfinite extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.autoLoadCount = 0;
        this.loadedUrls = new Set();
        this.onLoadMoreClick = this.onLoadMoreClick.bind(this);
      }

      connectedCallback() {
        // The append target is always the <ul> grid. On collection pages the id
        // "product-grid" sits on the <ul>; on the search page it sits on the
        // wrapping <div>, so resolve the list by class and read the section id
        // from #product-grid via the document (matches facets.js).
        this.grid = this.querySelector('ul.product-grid') || this.querySelector('.product-grid');
        this.controls = this.querySelector('.infinite-scroll');
        if (!this.grid || !this.controls) return;

        this.button = this.controls.querySelector('.infinite-scroll__more');
        this.status = this.controls.querySelector('.infinite-scroll__status');
        this.endEl = this.controls.querySelector('.infinite-scroll__end');
        this.sentinel = this.controls.querySelector('.infinite-scroll__sentinel');
        this.nextUrl = this.controls.getAttribute('data-next-url') || '';

        const gridIdEl = document.getElementById('product-grid');
        this.sectionId = (gridIdEl && gridIdEl.dataset.id) || this.grid.dataset.id || '';

        // 0 (default) = unlimited auto-load. Set a positive number to require a
        // manual "Load more" click after N automatic batches.
        this.autoLoadLimit = parseInt(this.getAttribute('data-auto-load-limit') || '0', 10);

        // Switch on the JS-enhanced UI: hides the numbered pagination and
        // shows the continuous-loading controls (see component-infinite-scroll.css).
        this.setAttribute('data-js-enabled', '');

        if (this.button) this.button.addEventListener('click', this.onLoadMoreClick);

        if (this.nextUrl) {
          this.showButton();
          this.setupObserver();
        } else {
          this.showEnd();
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.button) this.button.removeEventListener('click', this.onLoadMoreClick);
      }

      setupObserver() {
        if (!('IntersectionObserver' in window) || !this.sentinel) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              if (this.autoLoadLimit > 0 && this.autoLoadCount >= this.autoLoadLimit) return;
              this.autoLoadCount += 1;
              this.loadNext();
            });
          },
          // Load a little before the customer reaches the very bottom so the
          // grid grows smoothly, while still allowing the footer to be reached
          // once the end of the collection is hit.
          { rootMargin: '600px 0px 600px 0px' }
        );
        this.observer.observe(this.sentinel);
      }

      // Re-observe the sentinel so a fresh intersection callback fires if it is
      // still on screen after a load (e.g. tall viewports / short pages).
      rearmObserver() {
        if (!this.observer || !this.sentinel) return;
        this.observer.unobserve(this.sentinel);
        this.observer.observe(this.sentinel);
      }

      onLoadMoreClick(event) {
        event.preventDefault();
        this.autoLoadCount = 0; // a manual click refreshes the auto-load budget
        this.loadNext();
      }

      async loadNext() {
        if (this.loading || !this.nextUrl) return;
        if (this.loadedUrls.has(this.nextUrl)) {
          this.showEnd();
          return;
        }

        this.loading = true;
        this.loadedUrls.add(this.nextUrl);
        this.setBusy(true);

        const requestUrl = this.buildSectionUrl(this.nextUrl);

        try {
          const response = await fetch(requestUrl);
          if (!response.ok) throw new Error('HTTP ' + response.status);
          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');

          const newGrid = doc.querySelector('ul.product-grid');
          const newControls = doc.querySelector('.infinite-scroll');
          const items = newGrid ? Array.from(newGrid.children) : [];

          if (items.length) {
            const appended = items.map((item) => document.importNode(item, true));
            const fragment = document.createDocumentFragment();
            appended.forEach((node) => fragment.appendChild(node));
            this.grid.appendChild(fragment);

            // Reveal only the newly added cards. We deliberately do NOT re-run
            // Dawn's global initializeScrollAnimationTrigger over the whole grid,
            // as that would re-observe already-revealed cards and could hide ones
            // the customer has scrolled past.
            this.revealItems(appended);
          }

          this.nextUrl = newControls ? newControls.getAttribute('data-next-url') || '' : '';
          this.setBusy(false);

          if (this.nextUrl) {
            this.showButton();
          } else {
            this.showEnd();
          }
        } catch (error) {
          // Allow this page to be retried.
          this.loadedUrls.delete(this.nextUrl);
          this.setBusy(false);
          this.showError();
        } finally {
          this.loading = false;
          const withinAutoBudget = !(this.autoLoadLimit > 0 && this.autoLoadCount >= this.autoLoadLimit);
          if (this.nextUrl && withinAutoBudget) this.rearmObserver();
        }
      }

      // Reveal newly appended cards using the same intersection logic as Dawn's
      // animations.js, scoped to the given nodes only. No-op when the theme's
      // scroll animations are switched off (no .scroll-trigger elements).
      revealItems(nodes) {
        const triggers = [];
        nodes.forEach((node) => {
          if (node.classList && node.classList.contains('scroll-trigger')) triggers.push(node);
          if (node.getElementsByClassName) {
            triggers.push(...Array.from(node.getElementsByClassName('scroll-trigger')));
          }
        });
        if (!triggers.length) return;

        if (!('IntersectionObserver' in window)) {
          triggers.forEach((el) => el.classList.remove('scroll-trigger--offscreen'));
          return;
        }

        const observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry, index) => {
              const target = entry.target;
              if (entry.isIntersecting) {
                if (target.classList.contains('scroll-trigger--offscreen')) {
                  target.classList.remove('scroll-trigger--offscreen');
                  if (target.hasAttribute('data-cascade')) {
                    target.setAttribute('style', `--animation-order: ${index};`);
                  }
                }
                obs.unobserve(target);
              } else {
                target.classList.add('scroll-trigger--offscreen');
                target.classList.remove('scroll-trigger--cancel');
              }
            });
          },
          { rootMargin: '0px 0px -50px 0px' }
        );

        triggers.forEach((el) => observer.observe(el));
      }

      buildSectionUrl(url) {
        if (!this.sectionId) return url;
        try {
          const parsed = new URL(url, window.location.origin);
          parsed.searchParams.set('section_id', this.sectionId);
          return parsed.pathname + parsed.search;
        } catch (error) {
          const separator = url.indexOf('?') === -1 ? '?' : '&';
          return url + separator + 'section_id=' + encodeURIComponent(this.sectionId);
        }
      }

      setBusy(isBusy) {
        if (this.button) {
          this.button.classList.toggle('infinite-scroll__more--loading', isBusy);
          this.button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
          this.button.disabled = isBusy;
        }
        if (this.status) {
          this.status.textContent = isBusy ? this.status.dataset.loadingText || '' : '';
        }
      }

      showButton() {
        if (this.button) {
          this.button.hidden = false;
          this.button.disabled = false;
          this.button.classList.remove('infinite-scroll__more--error');
        }
        if (this.endEl) this.endEl.hidden = true;
      }

      showEnd() {
        if (this.observer) this.observer.disconnect();
        if (this.button) this.button.hidden = true;
        if (this.sentinel) this.sentinel.hidden = true;
        if (this.endEl) this.endEl.hidden = false;
        if (this.status) this.status.textContent = '';
      }

      showError() {
        if (this.button) {
          this.button.hidden = false;
          this.button.disabled = false;
          this.button.classList.add('infinite-scroll__more--error');
        }
        if (this.status) {
          this.status.textContent = this.status.dataset.errorText || '';
        }
      }
    }
  );
}
